/**
 * Cross-platform environment variable utility
 *
 * This utility safely retrieves environment variables in both:
 * 1. Node.js / React Native environments (using process.env)
 * 2. Vite / Browser environments (using import.meta.env)
 *
 * It prevents reference errors when accessing one method in the wrong environment.
 */

import type { AppEnvironment } from '../types';

/**
 * Get an environment variable safely from either process.env or import.meta.env
 *
 * @param key - The environment variable key
 * @param defaultValue - Optional default value if the variable is not set
 * @returns The value of the environment variable or the default value
 */
export interface EnvOptions<T = string> {
  defaultValue?: T;
  required?: boolean;
}

/**
 * Every `EXPO_PUBLIC_*` key the apps read through `getEnv`, as LITERAL reads.
 *
 * babel-preset-expo inlines a client variable only where it is written as
 * `process.env.EXPO_PUBLIC_X`; a computed `process.env[key]` is left alone. In
 * a dev bundle Metro also fills `process.env` at runtime, so the computed read
 * below works there, but a release bundle has nothing behind it and every value
 * silently falls back to its default (the first TestFlight build called the
 * placeholder API host this way). These literal reads are what a release bundle
 * actually carries.
 *
 * A new mobile env var needs a line here; `env.test.ts` fails on a `getEnv`
 * key without one. On web `process` is undefined, so the table is empty.
 */
function readExpoPublicEnv(): Record<string, string | undefined> {
  try {
    return {
      EXPO_PUBLIC_API_BASE_URL: process.env.EXPO_PUBLIC_API_BASE_URL,
      EXPO_PUBLIC_AUTH_BASE_URL: process.env.EXPO_PUBLIC_AUTH_BASE_URL,
      EXPO_PUBLIC_API_VERSION: process.env.EXPO_PUBLIC_API_VERSION,
      EXPO_PUBLIC_APP_ENVIRONMENT: process.env.EXPO_PUBLIC_APP_ENVIRONMENT,
      EXPO_PUBLIC_APP_VERSION: process.env.EXPO_PUBLIC_APP_VERSION,
      EXPO_PUBLIC_DEBUG_API: process.env.EXPO_PUBLIC_DEBUG_API,
      EXPO_PUBLIC_ENABLE_LOGGING: process.env.EXPO_PUBLIC_ENABLE_LOGGING,
      EXPO_PUBLIC_ENABLE_LIVE_NOTIFICATIONS:
        process.env.EXPO_PUBLIC_ENABLE_LIVE_NOTIFICATIONS,
      EXPO_PUBLIC_ENABLE_LIVE_JOB_PROGRESS:
        process.env.EXPO_PUBLIC_ENABLE_LIVE_JOB_PROGRESS,
      EXPO_PUBLIC_ENABLE_SOCIAL_SIGN_IN:
        process.env.EXPO_PUBLIC_ENABLE_SOCIAL_SIGN_IN,
      EXPO_PUBLIC_ENABLE_SOCIAL_LINKING:
        process.env.EXPO_PUBLIC_ENABLE_SOCIAL_LINKING,
      EXPO_PUBLIC_ENABLE_EMAIL_CHANGE:
        process.env.EXPO_PUBLIC_ENABLE_EMAIL_CHANGE,
      EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID:
        process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
      EXPO_PUBLIC_REVERB_APP_KEY: process.env.EXPO_PUBLIC_REVERB_APP_KEY,
      EXPO_PUBLIC_REVERB_HOST: process.env.EXPO_PUBLIC_REVERB_HOST,
      EXPO_PUBLIC_REVERB_PORT: process.env.EXPO_PUBLIC_REVERB_PORT,
      EXPO_PUBLIC_REVERB_SCHEME: process.env.EXPO_PUBLIC_REVERB_SCHEME,
      EXPO_PUBLIC_SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN,
    };
  } catch (_e) {
    return {};
  }
}

const EXPO_PUBLIC_ENV = readExpoPublicEnv();

/** The keys of the literal-read table, for the coverage test. */
export const EXPO_PUBLIC_ENV_KEYS: readonly string[] =
  Object.keys(EXPO_PUBLIC_ENV);

/** `VITE_X` → `EXPO_PUBLIC_X`; any other key gets the prefix prepended. */
export function toExpoPublicKey(key: string): string {
  return key.startsWith('VITE_')
    ? `EXPO_PUBLIC_${key.slice('VITE_'.length)}`
    : `EXPO_PUBLIC_${key}`;
}

/**
 * Get an environment variable safely from either process.env or import.meta.env
 *
 * @param key - The environment variable key
 * @param options - Options for retrieval (defaultValue, required) or just defaultValue string for backward compatibility
 * @returns The value of the environment variable or the default value
 * @throws Error if required is true and variable is missing
 */
export function getEnv(key: string, options?: EnvOptions): string | undefined {
  const defaultValue = options?.defaultValue;
  const required = options?.required || false;

  let value: string | undefined;

  // 1. Try process.env (Node.js/React Native)
  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      value = process.env[key];
    }
  } catch (_e) {
    // Ignore errors access process
  }

  // 1b. Try the Expo Public mirror of the key (React Native / Expo). Expo only
  // exposes client env vars prefixed `EXPO_PUBLIC_`, so each web `VITE_*` var is
  // mirrored as `EXPO_PUBLIC_*` (e.g. VITE_API_BASE_URL → EXPO_PUBLIC_API_BASE_URL).
  // Callers keep using the canonical `VITE_*` key; this resolves it on mobile.
  // The computed read covers dev bundles and tests (where `process.env` is
  // filled at runtime); the literal-read table covers release bundles.
  if (value === undefined) {
    const expoKey = toExpoPublicKey(key);
    try {
      if (
        typeof process !== 'undefined' &&
        process.env &&
        process.env[expoKey]
      ) {
        value = process.env[expoKey];
      }
    } catch (_e) {
      // Ignore errors accessing process
    }
    if (value === undefined && EXPO_PUBLIC_ENV[expoKey]) {
      value = EXPO_PUBLIC_ENV[expoKey];
    }
  }

  // 2. Try import.meta.env (Vite)
  if (value === undefined) {
    try {
      if (import.meta && import.meta.env && import.meta.env[key]) {
        value = import.meta.env[key];
      }
    } catch (_e) {
      // Ignore errors accessing import.meta
    }
  }

  // 3. Handle result
  if (value === undefined || value === '') {
    if (required) {
      throw new Error(`Environment variable ${key} is required but not set`);
    }
    return defaultValue;
  }

  return value;
}

/**
 * Get an environment variable as a number
 */
/**
 * Get an environment variable as a number
 */
export function getEnvNumber(
  key: string,
  options?: EnvOptions<number>
): number {
  const customOptions = { required: options?.required };
  const value = getEnv(key, customOptions);

  if (value === undefined || value === '') {
    if (options?.defaultValue !== undefined) {
      return options.defaultValue;
    }
    if (options?.required) {
      // Should have been caught by getEnv, but double check
      throw new Error(`Environment variable ${key} is required but not set`);
    }
    return NaN; // Or handle as expected for optional numbers
  }

  const parsed = Number(value);
  if (isNaN(parsed)) {
    if (options?.required) {
      throw new Error(`Environment variable ${key} must be a valid number`);
    }
    return options?.defaultValue !== undefined ? options.defaultValue : NaN;
  }

  return parsed;
}

/**
 * Get an environment variable as a boolean
 * Returns true if value is 'true', '1', 'yes', 'on' (case insensitive)
 */
export function getEnvBool(
  key: string,
  options?: EnvOptions<boolean>
): boolean {
  const customOptions = { required: options?.required };
  const value = getEnv(key, customOptions);

  if (value === undefined || value === '') {
    return options?.defaultValue ?? false;
  }

  const normalized = value.toLowerCase().trim();
  return ['true', '1', 'yes', 'on'].includes(normalized);
}

/**
 * Get an environment variable as a string array (comma-separated)
 */
export function getEnvArray(
  key: string,
  options?: EnvOptions<string[]>
): string[] {
  const customOptions = { required: options?.required };
  const value = getEnv(key, customOptions);

  if (value === undefined || value === '') {
    return options?.defaultValue || [];
  }

  return value.split(',').map((item) => item.trim());
}

/**
 * Get an environment variable safely from either process.env or import.meta.env
 *
 * Returns the default value if the environment variable is not set or empty.
 */
export function getEnvSafe(key: string, defaultValue: string): string {
  const value = getEnv(key);
  if (value === undefined || value === '') {
    return defaultValue;
  }
  return value;
}

const APP_ENVIRONMENTS: readonly AppEnvironment[] = [
  'development',
  'staging',
  'production',
  'e2e',
];

const toAppEnvironment = (value?: string): AppEnvironment | undefined =>
  APP_ENVIRONMENTS.find((environment) => environment === value);

/**
 * The environment the app is in, or whether it is one of those given —
 * modelled on Laravel's `app()->environment(...)`.
 *
 * One answer from one rule, so two checks can never disagree:
 * `VITE_APP_ENVIRONMENT` decides when it names an environment, `NODE_ENV`
 * when it does not (mobile sets no app variable), and anything else — a test
 * run included — is `development`.
 *
 * @example
 * appEnv(); // 'staging'
 * appEnv('production'); // false
 * appEnv('staging', 'production'); // true
 */
export function appEnv(): AppEnvironment;
export function appEnv(
  ...environments: [AppEnvironment, ...AppEnvironment[]]
): boolean;
export function appEnv(
  ...environments: AppEnvironment[]
): AppEnvironment | boolean {
  const current =
    toAppEnvironment(getEnv('VITE_APP_ENVIRONMENT')) ??
    toAppEnvironment(getEnv('NODE_ENV')) ??
    'development';

  return environments.length === 0 ? current : environments.includes(current);
}

/**
 * Safe check for test environment
 */
export const isTest = (): boolean => {
  return getEnv('NODE_ENV') === 'test' || getEnv('VITEST') === 'true';
};
